<template>
  <v-dialog
    :value="open"
    max-width="572"
    overlay-color="neutral lighten-1"
    overlay-opacity=".80"
    content-class="qr-dialog"
    scrollable
    @input="onDialogInput"
  >
    <v-card rounded class="pa-6">
      <v-card-title class="px-0 pt-0 pb-0">
        <p class="remove-payment-method-dialog__title mb-0" data-test="remove-payment-method-dialog-title">
          {{ $t('fields.account.billing.payment.payment_methods_remove_confirm_title') }}
        </p>
      </v-card-title>

      <v-card-text v-if="method" class="px-0 pt-3 pb-0" data-test="remove-payment-method-dialog-message">
        <p class="remove-payment-method-dialog__message mb-0">
          {{ confirmMessage }}
        </p>
      </v-card-text>

      <v-card-actions class="px-0 py-0 mt-6 justify-end">
        <v-btn
          outlined
          rounded
          :ripple="false"
          color="primary"
          large
          :disabled="isRemoving"
          data-test="remove-payment-method-dialog-cancel"
          @click="cancel"
        >
          {{ $t('fields.account.billing.payment.payment_methods_cancel') }}
        </v-btn>
        <v-btn
          depressed
          rounded
          :ripple="false"
          large
          class="remove-payment-method-dialog__confirm-btn ml-4"
          :loading="isRemoving"
          :disabled="!method"
          data-test="remove-payment-method-dialog-confirm"
          @click="confirm"
        >
          {{ $t('fields.account.billing.payment.payment_methods_remove') }}
        </v-btn>
      </v-card-actions>

      <Dismiss absolute top right @click.stop="cancel" />
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import type { SavedCardPaymentMethod, SavedPaymentMethod } from '@/classes/stripe';
import Dismiss from '@/components/Dismiss.vue';
import {
  getSavedPaymentMethodEmail,
  getSavedPaymentMethodTypeLabel,
} from '@/Services/PaymentMethod/paymentMethodLabels';
import { Component, Prop, Vue } from 'vue-property-decorator';

@Component({
  components: {
    Dismiss,
  },
})
export default class RemovePaymentMethodDialog extends Vue {
  @Prop(Boolean)
  open!: boolean;

  @Prop({ default: null })
  method!: SavedPaymentMethod | null;

  isRemoving = false;

  get cardMethod(): SavedCardPaymentMethod | null {
    return this.method?.type === 'card' ? this.method : null;
  }

  get confirmMessage(): string {
    if (!this.method) {
      return '';
    }

    const t = (key: string, params?: Record<string, string>) => this.$t(key, params).toString();

    const card = this.cardMethod;
    if (card) {
      const typeLabel = getSavedPaymentMethodTypeLabel(this.method, (key) => t(key));
      const isWallet = Boolean(card.is_wallet && card.wallet_type);

      if (isWallet) {
        return t('fields.account.billing.payment.payment_methods_remove_confirm_message_wallet', {
          wallet: typeLabel,
          last4: card.last4,
        });
      }

      return t('fields.account.billing.payment.payment_methods_remove_confirm_message_card', {
        brand: typeLabel,
        last4: card.last4,
      });
    }

    if (this.method.type === 'paypal' || this.method.type === 'klarna') {
      const email = getSavedPaymentMethodEmail(this.method);
      const key =
        this.method.type === 'paypal'
          ? 'fields.account.billing.payment.payment_methods_remove_confirm_message_paypal'
          : 'fields.account.billing.payment.payment_methods_remove_confirm_message_klarna';

      return t(key, { email });
    }

    return '';
  }

  onDialogInput(open: boolean): void {
    if (!open) {
      this.cancel();
    }
  }

  cancel(): void {
    if (this.isRemoving) {
      return;
    }
    this.$emit('cancel');
  }

  async confirm(): Promise<void> {
    if (!this.method || this.isRemoving || this.method.is_default) {
      return;
    }

    this.isRemoving = true;

    try {
      await this.$store.dispatch('subscription/deletePaymentMethod', this.method.id);
      await this.$store.dispatch('layout/setSnackbar', {
        type: 'info',
        icon: 'info-system',
        text: this.$t('fields.account.billing.payment.payment_methods_remove_success'),
        isSnackbar: true,
        active: true,
      });
      this.$emit('success', this.method);
    } catch {
      await this.$store.dispatch('layout/setSnackbar', {
        type: 'error',
        icon: 'forbidden-system',
        text: this.$t('fields.account.billing.payment.payment_methods_remove_error'),
        isSnackbar: false,
        active: true,
      });
      this.isRemoving = false;
      this.$emit('cancel');
      await this.$nextTick();
      this.scrollToTop();
      return;
    } finally {
      this.isRemoving = false;
    }
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }
}
</script>

<style lang="scss" scoped>
.remove-payment-method-dialog {
  &__title {
    font-size: rem(16);
    font-weight: 700;
    color: map-get($lapis, 'lighten-2');
    line-height: rem(22);
  }

  &__message {
    font-size: rem(14);
    color: map-get($neutral, 'darken-2');
    line-height: rem(20);
  }

  &__confirm-btn {
    background-color: #ef4f5e !important;
    color: #ffffff !important;
  }
}
</style>
